import DashboardLayout from '../components/DashboardLayout';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  AppBar,
  Toolbar
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { api, joinUrl } from '../api/config';

const NetsuiteSetup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    DATABASE_URL: '',
    NETSUITE_ACCOUNT_ID: '',
    NETSUITE_CONSUMER_KEY: '',
    NETSUITE_CONSUMER_SECRET: '',
    NETSUITE_TOKEN_ID: '',
    NETSUITE_TOKEN_SECRET: '',
    NETSUITE_BASE_URL: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState(false);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleClearField = (field) => () => {
    setFormData(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  const handleToggleConsumerSecretVisibility = () => {
    setShowConsumerSecret(!showConsumerSecret);
  };

  const handleToggleTokenSecretVisibility = () => {
    setShowTokenSecret(!showTokenSecret);
  };

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Basic validation - check if all required fields are filled
      const requiredFields = [
        'DATABASE_URL',
        'NETSUITE_ACCOUNT_ID', 
        'NETSUITE_CONSUMER_KEY',
        'NETSUITE_CONSUMER_SECRET',
        'NETSUITE_TOKEN_ID',
        'NETSUITE_TOKEN_SECRET',
        'NETSUITE_BASE_URL'
      ];
      
      const emptyFields = requiredFields.filter(field => !formData[field]?.trim());
      
      if (emptyFields.length > 0) {
        const fieldNames = emptyFields.map(field => 
          field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        ).join(', ');
        throw new Error(`Please fill in all required fields: ${fieldNames}`);
      }
      
      // Get the base URL and token from environment/session
      const baseUrl = import.meta.env.VITE_API_URL;
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Prepare the request data in the required order
      const requestData = {
        RAILWAY_DATABASE_URL: formData.DATABASE_URL,
        NETSUITE_ACCOUNT_ID: formData.NETSUITE_ACCOUNT_ID,
        NETSUITE_CONSUMER_KEY: formData.NETSUITE_CONSUMER_KEY,
        NETSUITE_CONSUMER_SECRET: formData.NETSUITE_CONSUMER_SECRET,
        NETSUITE_TOKEN_ID: formData.NETSUITE_TOKEN_ID,
        NETSUITE_TOKEN_SECRET: formData.NETSUITE_TOKEN_SECRET,
        NETSUITE_BASE_URL: formData.NETSUITE_BASE_URL
      };
      
      console.log('Sending request data:', requestData);
      console.log('Request URL:', '/provide-env-data');
      console.log('Request headers:', {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
      
      // Make the API call
      const response = await api.post('/provide-env-data', requestData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200 || response.status === 201) {
        setSuccess('NetSuite configuration saved successfully!');
        console.log('NetSuite Setup Response:', response.data);
      } else {
        throw new Error('Failed to save configuration');
      }
      
    } catch (err) {
      console.error('NetSuite Setup Error:', err);
      console.error('Full error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response detail:', err.response?.data?.detail);
      
      // Handle 422 validation errors
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const validationErrors = err.response.data.detail;
        console.error('Validation errors array:', validationErrors);
        
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map(error => {
            console.error('Processing error:', error);
            if (typeof error === 'string') {
              return error;
            } else if (error.msg) {
              return `${error.loc?.join('.') || 'Field'}: ${error.msg}`;
            }
            return JSON.stringify(error);
          }).join(', ');
          setError(`Validation errors: ${errorMessages}`);
        } else {
          setError(`Validation error: ${JSON.stringify(validationErrors)}`);
        }
      } 
      // Handle 400 NetSuite credential errors
      else if (err.response?.status === 400 && err.response?.data?.detail) {
        setError(err.response.data.detail);
      }
      else {
        setError(err.response?.data?.message || err.message || 'Failed to save NetSuite configuration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

    return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'white' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            NetSuite Setup
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: 'white',
        pt: 4,
        px: 2
      }}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 6, 
            width: '100%',
            maxWidth: 1200,
            backgroundColor: 'white'
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            NetSuite Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Configure your NetSuite integration settings
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Database URL */}
            <TextField
              fullWidth
              label="Database URL"
              value={formData.DATABASE_URL}
              onChange={handleInputChange('DATABASE_URL')}
              placeholder="Enter your database URL"
              variant="outlined"
              InputProps={{
                endAdornment: formData.DATABASE_URL && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearField('DATABASE_URL')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Account ID */}
            <TextField
              fullWidth
              label="NetSuite Account ID"
              value={formData.NETSUITE_ACCOUNT_ID}
              onChange={handleInputChange('NETSUITE_ACCOUNT_ID')}
              placeholder="Enter your NetSuite Account ID"
              variant="outlined"
              InputProps={{
                endAdornment: formData.NETSUITE_ACCOUNT_ID && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearField('NETSUITE_ACCOUNT_ID')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Consumer Key */}
            <TextField
              fullWidth
              label="NetSuite Consumer Key"
              value={formData.NETSUITE_CONSUMER_KEY}
              onChange={handleInputChange('NETSUITE_CONSUMER_KEY')}
              placeholder="Enter your NetSuite Consumer Key"
              variant="outlined"
              InputProps={{
                endAdornment: formData.NETSUITE_CONSUMER_KEY && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearField('NETSUITE_CONSUMER_KEY')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Consumer Secret */}
            <TextField
              fullWidth
              label="NetSuite Consumer Secret"
              value={formData.NETSUITE_CONSUMER_SECRET}
              onChange={handleInputChange('NETSUITE_CONSUMER_SECRET')}
              placeholder="Enter your NetSuite Consumer Secret"
              variant="outlined"
              type={showConsumerSecret ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleToggleConsumerSecretVisibility}
                      edge="end"
                    >
                      {showConsumerSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    {formData.NETSUITE_CONSUMER_SECRET && (
                      <IconButton
                        size="small"
                        onClick={handleClearField('NETSUITE_CONSUMER_SECRET')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Token ID */}
            <TextField
              fullWidth
              label="NetSuite Token ID"
              value={formData.NETSUITE_TOKEN_ID}
              onChange={handleInputChange('NETSUITE_TOKEN_ID')}
              placeholder="Enter your NetSuite Token ID"
              variant="outlined"
              InputProps={{
                endAdornment: formData.NETSUITE_TOKEN_ID && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearField('NETSUITE_TOKEN_ID')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Token Secret */}
            <TextField
              fullWidth
              label="NetSuite Token Secret"
              value={formData.NETSUITE_TOKEN_SECRET}
              onChange={handleInputChange('NETSUITE_TOKEN_SECRET')}
              placeholder="Enter your NetSuite Token Secret"
              variant="outlined"
              type={showTokenSecret ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleToggleTokenSecretVisibility}
                      edge="end"
                    >
                      {showTokenSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    {formData.NETSUITE_TOKEN_SECRET && (
                      <IconButton
                        size="small"
                        onClick={handleClearField('NETSUITE_TOKEN_SECRET')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            
            {/* NetSuite Base URL */}
            <TextField
              fullWidth
              label="NetSuite Base URL"
              value={formData.NETSUITE_BASE_URL}
              onChange={handleInputChange('NETSUITE_BASE_URL')}
              placeholder="Enter your NetSuite Base URL"
              variant="outlined"
              InputProps={{
                endAdornment: formData.NETSUITE_BASE_URL && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearField('NETSUITE_BASE_URL')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
            
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSetup}
                disabled={loading}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  minWidth: 200
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Setting up...
                  </>
                ) : (
                  'Setup'
                )}
              </Button>
            </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default NetsuiteSetup; 